import { getColorScheme, getGlobalState } from '../Gui/store/persistent/persistentStore.js';
import * as THREE from 'three';
import Grid from './grid';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import FVector from '@data/geometry/FVector.js';
import { getGrid } from '../store/LoadingStore.js';
import { GroundRoadTextures } from '@data/geodata/legacy/GroundRoadTextures.js';
import { TERRAIN_LINE_KIND } from '@visualizer/types';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { fromCurrentCoordinate, resolveCoordinateSystemSelection } from '@data/geodata/CoordinateSystemUtils';
import { getViewer } from 'store/viewerStore';

type MetricCoordinate = {
    x: number;
    y: number;
    z: number;
};

// =============== MESH GENERATORS ===============
function createLinesMesh(vertices: Float32Array, colorNumber: number, linewidth: number = 2): LineSegments2 {
    const color: THREE.Color = new THREE.Color(colorNumber);

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(vertices);

    const material = new LineMaterial({
        color,
        linewidth,
        resolution: new THREE.Vector2(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio),
    });

    const line = new LineSegments2(geometry, material);
    return line;
}

function createColoredLineMesh(vertices: Float32Array, colors: number[], linewidth: number = 2): LineSegments2 {
    // Create geometry:
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(vertices);
    geometry.setColors(colors);

    // Create Material:
    const material = new LineMaterial({
        vertexColors: true,
        linewidth: linewidth,
        resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    });

    return new LineSegments2(geometry, material);
}

function createTrianglesMesh(vertices: Float32Array, indices: number[], meshColor: THREE.Color | number = 0xe1e1e1): THREE.Mesh {
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

    const material = new THREE.MeshBasicMaterial({
        color: meshColor,
        side: THREE.DoubleSide,
        polygonOffset: true,
        depthTest: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
    });

    return new THREE.Mesh(geometry, material);
}

// =============== MAIN CLASS ===============
export class ContextItems {
    siteCenterMesh: LineSegments2;
    boundingBox: LineSegments2;
    analysisBox: LineSegments2;

    siteAxes: LineSegments2;
    windStation: THREE.Group;

    constructor(scene: THREE.Scene) {
        const colorScheme = getColorScheme();

        this.siteCenterMesh = this.drawVerticalLine(Grid.siteVolumeCenter, colorScheme.SITE_CENTER);
        const boundingBoxMap = this.convertArrayToMap(Grid.boundingBox);
        this.boundingBox = this.generateBox(boundingBoxMap, Grid.gridRes * (Grid.maxFloor + 1), colorScheme.SITE_BOX);
        this.analysisBox = this.generateBox(Grid.analysisArea, Grid.gridRes * Grid.gridZ, colorScheme.SITE_BOX);

        // Site axes, reactive to CCRS changes:
        this.siteAxes = this.generateSiteAxes(new FVector(), 5);
        this.updateSiteAxesTransform();
        usePersistentStore.subscribe(
            (state) => state.global.settings.coordinateSystem,
            () => {
                this.updateSiteAxesTransform();
                getViewer().requestRender('ui');
            }
        );

        const windStationOrigin = new FVector(1000, 1000, 100);
        this.windStation = this.generateWindStation(windStationOrigin, 100, colorScheme.WIND_STATION_BOX, colorScheme.ANALYSIS_BORDER);

        this.addToScene(scene);
    }

    // =============== GENERATORS FUNCTIONS ===============
    private borderToSegmentVertices(border: Map<string, number[]>): Float32Array {
        const vertices: number[] = [];
        const x = border.get('x') || [];
        const y = border.get('y') || [];
        const z = border.get('z') || [];
        for (let i = 0; i < x.length; i++) {
            const nextKey = (i + 1) % x.length;
            vertices.push(x[i], y[i], z[i]);
            vertices.push(x[nextKey], y[nextKey], z[nextKey]);
        }
        return new Float32Array(vertices);
    }

    private drawVerticalLine(point: MetricCoordinate, color: number): LineSegments2 {
        const vertices: Float32Array = new Float32Array([point.x, point.y, point.z, point.x, point.y, point.z + 10000]);

        return createLinesMesh(vertices, color);
    }

    private generateBox(border: Map<string, number[]>, height: number, color: number): LineSegments2 {
        const vertices: number[] = [];

        const x = border.get('x')!;
        const y = border.get('y')!;
        const z = border.get('z')!;
        const length = x.length;

        // Floor:
        for (let i = 0; i < length; i++) {
            const next = (i + 1) % length;
            vertices.push(x[i], y[i], z[i]);
            vertices.push(x[next], y[next], z[next]);
        }

        // Roof:
        for (let i = 0; i < length; i++) {
            const next = (i + 1) % length;
            vertices.push(x[i], y[i], z[i] + height);
            vertices.push(x[next], y[next], z[next] + height);
        }

        // Walls:
        for (let i = 0; i < length; i++) {
            vertices.push(x[i], y[i], z[i]);
            vertices.push(x[i], y[i], z[i] + height);
        }

        return createLinesMesh(new Float32Array(vertices), color);
    }

    private generateSiteAxes(center: FVector, size: number): LineSegments2 {
        // Helper to offset points by center:
        const offset = (x: number, y: number, z: number) => [x + center.x, y + center.y, z + center.z];

        // Vertices for all lines (two points per line, for both main axis + arrows):
        const verticesArray: number[] = [
            // Y axis (green)
            ...offset(0, 0, 0),
            ...offset(0, size, 0), // main axis line
            ...offset(0, size, 0),
            ...offset(size / 3, size / 2, 0), // arrow side 1
            ...offset(0, size, 0),
            ...offset(-size / 3, size / 2, 0), // arrow side 2

            // X axis (red)
            ...offset(0, 0, 0),
            ...offset(size, 0, 0),
            ...offset(size, 0, 0),
            ...offset(size / 2, size / 3, 0),
            ...offset(size, 0, 0),
            ...offset(size / 2, -size / 3, 0),

            // Z axis (blue)
            ...offset(0, 0, 0),
            ...offset(0, 0, size),
            ...offset(0, 0, size),
            ...offset(0, size / 3, size / 2),
            ...offset(0, 0, size),
            ...offset(0, -size / 3, size / 2),
        ];

        // Colors correspond to each vertex pair (two points per line):
        const green = [0, 1, 0];
        const red = [1, 0, 0];
        const blue = [0, 0, 1];

        // Assign color to each vertex:
        const colors: number[] = [
            ...green,
            ...green, // Y main
            ...green,
            ...green, // Y arrow 1
            ...green,
            ...green, // Y arrow 2

            ...red,
            ...red, // X main
            ...red,
            ...red, // X arrow 1
            ...red,
            ...red, // X arrow 2

            ...blue,
            ...blue, // Z main
            ...blue,
            ...blue, // Z arrow 1
            ...blue,
            ...blue, // Z arrow 2
        ];

        const vertices = new Float32Array(verticesArray);

        return createColoredLineMesh(new Float32Array(vertices), colors);
    }

    private updateSiteAxesTransform(): void {
        const coordinateSystem = resolveCoordinateSystemSelection(getGlobalState().settings.coordinateSystem);

        if (coordinateSystem.mode === 'custom') {
            const localOrigin = fromCurrentCoordinate({ x: 0, y: 0, z: 0 }, coordinateSystem);
            this.siteAxes.position.set(localOrigin.x, localOrigin.y, localOrigin.z);
            this.siteAxes.rotation.set(0, 0, THREE.MathUtils.degToRad(coordinateSystem.alpha));
            return;
        }

        this.siteAxes.position.set(0, 0, 0);
        this.siteAxes.rotation.set(0, 0, 0);
    }

    private generateWindStation(center: FVector, size: number, meshColor: number, strokeColor: number): THREE.Group {
        const half = size;

        const rawVertices = [
            -half,
            -half,
            half, // 0
            half,
            -half,
            half, // 1
            -half,
            half,
            half, // 2
            half,
            half,
            half, // 3
            -half,
            -half,
            -half, // 4
            half,
            -half,
            -half, // 5
            -half,
            half,
            -half, // 6
            half,
            half,
            -half, // 7
        ];

        const vertices = new Float32Array(
            rawVertices.map((v, i) => {
                if (i % 3 === 0) return v + center.x;
                if (i % 3 === 1) return v + center.y;
                return v + center.z;
            })
        );

        const indices = [
            // Top
            2, 3, 7, 2, 7, 6,
            // Bottom
            0, 4, 5, 5, 1, 0,
            // Left
            4, 0, 2, 4, 2, 6,
            // Right
            1, 5, 7, 1, 7, 3,
            // Front
            0, 1, 3, 0, 3, 2,
            // Back
            4, 6, 7, 7, 5, 4,
        ];

        const boxMesh = createTrianglesMesh(vertices, indices, meshColor);

        const expandedVertices = this.buildLineVerticesFromIndices(vertices, indices);
        const wireMesh = createLinesMesh(new Float32Array(expandedVertices), strokeColor, 1);

        const group = new THREE.Group();
        group.add(boxMesh, wireMesh);

        return group;
    }

    // =============== MAIN FUNCTIONS ===============
    public addToScene(scene: THREE.Scene): void {
        const collection = new THREE.Group();

        collection.add(this.siteCenterMesh);

        collection.add(this.boundingBox);
        collection.add(this.analysisBox);

        collection.add(this.siteAxes);
        collection.add(this.windStation);

        if (scene) scene.add(collection);
    }

    public toggleVisibility(): void {
        const { context } = getGlobalState();

        const ground = getGrid().getActiveGeodata().dataObjectLegacy.Ground;
        ground?.updateTerrainlineShader();

        this.siteCenterMesh.visible = context.showGridCenter;
        this.boundingBox.visible = context.showSiteBox;
        this.analysisBox.visible = context.showAnalysisVolume;

        this.siteAxes.visible = context.showGridAxes;
        this.windStation.visible = context.showWindStations;
    }

    public syncTerrainLines(): void {
        const ground = getGrid().getActiveGeodata().dataObjectLegacy.Ground;
        if (!ground) return;

        ground.addTerrainLines(
            createLinesMesh(this.borderToSegmentVertices(Grid.legalBorder), getColorScheme().LEGAL_BORDER),
            GroundRoadTextures.POLYGON_HALF_WIDTH,
            getColorScheme().LEGAL_BORDER,
            TERRAIN_LINE_KIND.LEGAL_BORDER
        );
        ground.addTerrainLines(
            createLinesMesh(this.borderToSegmentVertices(Grid.buildableBorder), getColorScheme().BUILDABLE_BORDER),
            GroundRoadTextures.POLYGON_HALF_WIDTH,
            getColorScheme().BUILDABLE_BORDER,
            TERRAIN_LINE_KIND.BUILDABLE_BORDER
        );
        ground.addTerrainLines(
            createLinesMesh(this.borderToSegmentVertices(Grid.analysisArea), getColorScheme().ANALYSIS_BORDER),
            GroundRoadTextures.POLYGON_HALF_WIDTH,
            getColorScheme().ANALYSIS_BORDER,
            TERRAIN_LINE_KIND.ANALYSIS_BORDER
        );
        ground.updateTerrainlineShader();
    }

    // =============== HELPER FUNCTIONS ===============
    private convertArrayToMap(points: { x: number; y: number; z: number }[]): Map<string, number[]> {
        const map = new Map<string, number[]>();
        map.set(
            'x',
            points.map((p) => p.x)
        );
        map.set(
            'y',
            points.map((p) => p.y)
        );
        map.set(
            'z',
            points.map((p) => p.z)
        );
        return map;
    }

    private buildLineVerticesFromIndices(vertices: Float32Array, indices: number[]): number[] {
        const lineVertices: number[] = [];

        for (let i = 0; i < indices.length; i += 3) {
            const [a, b, c] = [indices[i], indices[i + 1], indices[i + 2]];

            // Triangle edges: a-b, b-c, c-a
            [
                [a, b],
                [b, c],
                [c, a],
            ].forEach(([start, end]) => {
                for (const index of [start, end]) {
                    const i3 = index * 3;
                    lineVertices.push(vertices[i3], vertices[i3 + 1], vertices[i3 + 2]);
                }
            });
        }

        return lineVertices;
    }
}
